import React, { useCallback, useState } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Label } from '@fluentui/react';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ValidatedJobFunctionPicker, ValidatedIndustryPicker, ValidatedCompanyPicker } from '@bingads-webui-campaign-react/display-plus-native-ads';

import { getDemographicPickerStyle } from '../styles/demographic-picker';
import { ProfileType } from '../constants/profile';

const UnstyledDemographicPicker = (props) => {
  const {
    className,
    classes,
    i18n,
    title,
    description,
    profiles,
    popupId,
    type,
  } = props;

  const [initialProfiles, setInitialProfiles] = useState(profiles);

  const isSameProfile = useCallback((profile1, profile2) => {
    if (type === ProfileType.CompanyName) {
      return _.isEqual(_.sortBy(_.pluck(profile1, 'Target')), _.sortBy(_.pluck(profile2, 'Target')));
    }

    return _.isEqual(_.sortBy(profile1), _.sortBy(profile2));
  }, [type]);

  const onValidate = useCallback((result) => {
    // need to clear initialProfiles after the selected profiles are changed,
    // or the initialProfiles will be repeatedly added to the selected profiles.
    if (!_.isEmpty(initialProfiles) && !isSameProfile(initialProfiles, _.get(result, ['data', [type]]))) {
      setInitialProfiles([]);
    }
  }, [initialProfiles, isSameProfile, type]);

  return (
    <div className={classNames([className, classes.demographicPickerContainer])}>
      <Label>
        {title}
        {popupId && <HelpPopup
          aria-label={title}
          topic={popupId}
        />}
      </Label>
      <div>{description || null}</div>
      {
        (type === ProfileType.CompanyName) && (
          <ValidatedCompanyPicker
            classes={classes}
            i18n={i18n}
            showExclusion={false}
            validationProps={{
              data: {
                companyNameTargets: initialProfiles,
                negativeCompanyNameTargets: [],
              },
              onValidate,
            }}
          />
        )
      }
      {
        (type === ProfileType.Industry) && (
          <ValidatedIndustryPicker
            classes={classes}
            i18n={i18n}
            showExclusion={false}
            validationProps={{
              data: {
                industry: initialProfiles,
                negativeIndustry: [],
              },
              onValidate,
            }}
          />
        )
      }
      {
        (type === ProfileType.JobFunction) && (
          <ValidatedJobFunctionPicker
            classes={classes}
            i18n={i18n}
            showExclusion={false}
            validationProps={{
              data: {
                jobTargets: initialProfiles,
                negativeJobTargets: [],
              },
              onValidate,
            }}
          />
        )
      }
    </div>
  );
};

UnstyledDemographicPicker.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({
    demographicPickerContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  profiles: PropTypes.arrayOf(PropTypes.shape([])),
  popupId: PropTypes.string,
  type: PropTypes.string.isRequired,
};

UnstyledDemographicPicker.defaultProps = {
  className: '',
  description: '',
  profiles: [],
  popupId: '',
};

export const DemographicPicker = withDefaultStyles(UnstyledDemographicPicker, getDemographicPickerStyle);
